namespace $ {
	export type $shm_projects_record_event_stream_item = {
		id?: string
		sequence?: number
	}

	export type $shm_projects_record_event_stream_state = {
		cursor: number
		count: number
		sequenced: boolean
		last_id: string
	}

	export type $shm_projects_record_event_stream_patch< Event > = {
		items: readonly Event[]
		reset: boolean
		state: $shm_projects_record_event_stream_state
	}

	export function $shm_projects_record_event_stream_state(
		events: readonly $shm_projects_record_event_stream_item[],
	): $shm_projects_record_event_stream_state {
		const last = events.at( -1 )
		return {
			cursor: events.reduce( ( max, event ) => Math.max( max, event.sequence ?? 0 ), 0 ),
			count: events.length,
			sequenced: events.every( event => Boolean( event.id ) && Number.isSafeInteger( event.sequence ) && event.sequence! > 0 ),
			last_id: last?.id ?? '',
		}
	}

	export function $shm_projects_record_event_stream_patch< Event extends $shm_projects_record_event_stream_item >(
		events: readonly Event[],
		previous: $shm_projects_record_event_stream_state,
		limit = 500,
	): $shm_projects_record_event_stream_patch< Event > {
		const state = $shm_projects_record_event_stream_state( events )
		const appended = events.filter( event => ( event.sequence ?? 0 ) > previous.cursor )
		const expected = Math.min( limit, previous.count + appended.length )
		const reset = !previous.sequenced || !state.sequenced
			|| state.cursor < previous.cursor
			|| state.cursor === previous.cursor && ( state.count !== previous.count || state.last_id !== previous.last_id )
			|| state.cursor > previous.cursor && state.count !== expected
		return { items: reset ? events : appended, reset, state }
	}

	export function $shm_projects_record_event_stream_merge< Event extends $shm_projects_record_event_stream_item >(
		current: readonly Event[],
		patch: readonly Event[],
		reset: boolean,
		count: number,
	): readonly Event[] {
		if( count <= 0 ) return []
		if( reset ) return patch.slice( -count )
		const known = new Set( current.map( event => event.id ).filter( Boolean ) )
		return [ ...current, ...patch.filter( event => !event.id || !known.has( event.id ) ) ].slice( -count )
	}

	export function $shm_projects_record_event_stream_append<
		Event extends object,
	>(
		events: readonly ( Event & $shm_projects_record_event_stream_item )[],
		prefix: string,
		event: Event & $shm_projects_record_event_stream_item,
		limit = 500,
	): readonly ( Event & $shm_projects_record_event_stream_item )[] {
		let sequence = events.reduce( ( max, item ) => {
			const value = Number.isSafeInteger( item.sequence ) && item.sequence! > 0 ? item.sequence! : 0
			return Math.max( max, value )
		}, 0 )
		const normalized = events.map( item => {
			const current = Number.isSafeInteger( item.sequence ) && item.sequence! > 0
				? item.sequence!
				: ++sequence
			return item.id && item.sequence === current
				? item
				: { ...item, id: item.id || `${ prefix }:${ current }`, sequence: current }
		} )
		sequence = normalized.reduce( ( max, item ) => Math.max( max, item.sequence ?? 0 ), sequence )
		if( event.id ) {
			const duplicate = normalized.find( item => item.id === event.id )
			if( duplicate ) {
				const replay = { ...event, sequence: duplicate.sequence }
				if( !$mol_compare_deep( duplicate, replay ) ) throw new Error( `Event identity conflict: ${ event.id }` )
				return normalized.slice( -Math.max( 1, limit ) )
			}
		}
		const requested = Number.isSafeInteger( event.sequence ) && event.sequence! > sequence
			? event.sequence!
			: sequence + 1
		const next = {
			...event,
			id: event.id || `${ prefix }:${ requested }`,
			sequence: requested,
		}
		return [ ...normalized, next ].slice( -Math.max( 1, limit ) )
	}
}
